import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AttendanceService } from '../../services/attendance.service';
import { MatSnackBar } from '@angular/material/snack-bar';

interface Employee {
  id: number;
  firstName: string;
  lastName: string;
  fingerprintId?: string;
}

interface DailyManualAttendance {
  date: string;
  dayNumber: number;
  login1?: string | null;
  logout1?: string | null;
  login2?: string | null;
  logout2?: string | null;
  login3?: string | null;
  logout3?: string | null;
  breaksMinutes?: number | null;
  totalHours: number;
  hasExistingData: boolean;
}

interface MonthlyManualAttendance {
  employeeId: number;
  employeeName: string;
  fingerprintId: string;
  year: number;
  month: number;
  dailyEntries: DailyManualAttendance[];
  totalMonthlyHours: number;
}

@Component({
  selector: 'app-manual-attendance-entry',
  templateUrl: './manual-attendance-entry.component.html',
  styleUrls: ['./manual-attendance-entry.component.scss']
})
export class ManualAttendanceEntryComponent implements OnInit {
  selectionForm: FormGroup;
  employees: Employee[] = [];
  monthlyData: MonthlyManualAttendance | null = null;
  isLoading = false;
  isSaving = false;
  currentYear = new Date().getFullYear();
  currentMonth = new Date().getMonth() + 1;

  // Month names for display
  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  constructor(
    private fb: FormBuilder,
    private attendanceService: AttendanceService,
    private snackBar: MatSnackBar
  ) {
    this.selectionForm = this.fb.group({
      employeeId: ['', Validators.required],
      year: [this.currentYear, Validators.required],
      month: [this.currentMonth, Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadEmployees();
  }

  private loadEmployees(): void {
    this.isLoading = true;
    this.attendanceService.getAllEmployees(true).subscribe({
      next: (response) => {
        this.employees = response.employees || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  loadMonthlyData(): void {
    if (this.selectionForm.valid) {
      const { employeeId, year, month } = this.selectionForm.value;
      this.isLoading = true;

      this.attendanceService.getMonthlyManualAttendance(employeeId, year, month).subscribe({
        next: (response) => {
          this.monthlyData = response;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading monthly data:', error);
          this.snackBar.open('Error loading monthly data', 'Close', { duration: 3000 });
          this.isLoading = false;
        }
      });
    }
  }

  onTimeChange(dayIndex: number, field: string, value: string): void {
    if (this.monthlyData) {
      const day = this.monthlyData.dailyEntries[dayIndex];
      const cleanValue = value || null;
      
      switch (field) {
        case 'login1':
          day.login1 = cleanValue;
          break;
        case 'logout1':
          day.logout1 = cleanValue;
          break;
        case 'login2':
          day.login2 = cleanValue;
          break;
        case 'logout2':
          day.logout2 = cleanValue;
          break;
        case 'login3':
          day.login3 = cleanValue;
          break;
        case 'logout3':
          day.logout3 = cleanValue;
          break;
      }
      
      this.calculateDayHours(dayIndex);
      this.calculateMonthlyTotal();
    }
  }

  onBreaksChange(dayIndex: number, value: number): void {
    if (this.monthlyData) {
      this.monthlyData.dailyEntries[dayIndex].breaksMinutes = value || null;
      this.calculateDayHours(dayIndex);
      this.calculateMonthlyTotal();
    }
  }

  private calculateDayHours(dayIndex: number): void {
    if (!this.monthlyData) return;

    const day = this.monthlyData.dailyEntries[dayIndex];
    let totalMinutes = 0;

    // Calculate time for each login/logout pair
    const timePairs = [
      { login: day.login1, logout: day.logout1 },
      { login: day.login2, logout: day.logout2 },
      { login: day.login3, logout: day.logout3 }
    ];

    timePairs.forEach(pair => {
      if (pair.login && pair.logout) {
        const loginTime = this.parseTime(pair.login);
        const logoutTime = this.parseTime(pair.logout);
        
        if (loginTime !== null && logoutTime !== null && logoutTime > loginTime) {
          totalMinutes += (logoutTime - loginTime);
        }
      }
    });

    // Subtract break time
    const breakMinutes = day.breaksMinutes || 0;
    totalMinutes = Math.max(0, totalMinutes - breakMinutes);

    // Convert to hours
    day.totalHours = Math.round((totalMinutes / 60) * 100) / 100;
  }

  private parseTime(timeStr: string | null): number | null {
    if (!timeStr) return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private calculateMonthlyTotal(): void {
    if (!this.monthlyData) return;

    this.monthlyData.totalMonthlyHours = this.monthlyData.dailyEntries
      .reduce((total, day) => total + day.totalHours, 0);
  }

  saveAllChanges(): void {
    if (!this.monthlyData) return;

    this.isSaving = true;
    const saveData = {
      employeeId: this.monthlyData.employeeId,
      year: this.monthlyData.year,
      month: this.monthlyData.month,
      dailyEntries: this.monthlyData.dailyEntries
    };

    this.attendanceService.saveMonthlyManualAttendance(saveData).subscribe({
      next: (response) => {
        this.snackBar.open(
          `Successfully saved ${response.recordsCreated} records`, 
          'Close', 
          { duration: 3000 }
        );
        this.isSaving = false;
        // Reload data to reflect changes
        this.loadMonthlyData();
      },
      error: (error) => {
        console.error('Error saving monthly data:', error);
        this.snackBar.open('Error saving monthly data', 'Close', { duration: 3000 });
        this.isSaving = false;
      }
    });
  }

  getSelectedEmployeeName(): string {
    const employeeId = this.selectionForm.get('employeeId')?.value;
    const employee = this.employees.find(emp => emp.id === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : '';
  }

  getMonthName(month: number): string {
    return this.monthNames[month - 1] || '';
  }
}